import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { assetsClipRetrieve, ClipAssetResponse } from '@goldcast/api/content';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import ClipPreviewModal from './ClipPreviewModal';
import ClipCardSkeleton from './ClipCardSkeleton';
import ClipRecommendationEmptyState from './ClipRecommendationEmptyState';
import { HomepageCarouselStatusData, TopicGroup } from './types';
import ClipSection from './ClipSection';
import useDialog from '@/components/organisms/useDialog';
import { useAppContext } from '@/context/AppContext/AppContext';
import {
  clipRecommendationsLoadingStore,
  loadClipRecommendations,
  getTopicsAsArray,
  clipRecommendationsStore,
  isSavedSearchIdAlreadyDone
} from '@/stores/clipRecommendations';
import { showErrorToast } from '@/libs/toast/toast';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import Icon from '@/components/atoms/Icon';

const ClipRecommendation = () => {
  const [selectedClip, setSelectedClip] = useState<Clip | null>(null);
  const [isClipLoading, setIsClipLoading] = useState(false);

  const _clipRecommendations = useSyncExternalStore(
    clipRecommendationsStore.subscribe,
    clipRecommendationsStore.getSnapshot
  );

  const isLoading = useSyncExternalStore(
    clipRecommendationsLoadingStore.subscribe,
    clipRecommendationsLoadingStore.getSnapshot
  );

  const topicsArray = useMemo(() => getTopicsAsArray(), [_clipRecommendations]);

  const isAllTopicsEmpty = useMemo(() => {
    if (topicsArray.length === 0) {
      return true;
    }
    return topicsArray.every((topicGroup: TopicGroup) => topicGroup.results.length === 0);
  }, [topicsArray]);

  const hasLimitedContent = useMemo(() => {
    const topicsWithResults = topicsArray.filter((topic: TopicGroup) => topic.results.length > 0);
    if (topicsWithResults.length !== 1) {
      return false;
    }
    return topicsWithResults[0].results.length <= 6;
  }, [topicsArray]);

  const { isOpen: isPreviewModalOpen, closeDialog: closePreviewModal, openDialog: openPreviewModal } = useDialog();

  const organization = useAppContext().adminAppStore.user?.organization ?? '';

  useEffect(() => {
    if (organization) {
      loadClipRecommendations(organization);
    }
  }, [organization]);

  useEffect(() => {
    const listener = EventBus.on(CustomEvents.HomepageCarouselStatusUpdated, (data: HomepageCarouselStatusData) => {
      if (data.status === 'done' && organization && !isSavedSearchIdAlreadyDone(data.saved_search_id)) {
        loadClipRecommendations(organization, false);
      }
    });
    return () => EventBus.off(CustomEvents.HomepageCarouselStatusUpdated, listener);
  }, [organization]);

  const handleClipClick = (clip: Clip) => {
    setIsClipLoading(true);
    openPreviewModal();
    assetsClipRetrieve({ id: clip.id })
      .then((clip: ClipAssetResponse) => {
        setSelectedClip(clip as Clip);
        setIsClipLoading(false);
      })
      .catch(() => {
        setIsClipLoading(false);
        closePreviewModal();
        showErrorToast('Error retrieving clip. Please try again.');
      });
  };

  const handleUpdateSelectedClip = (clip: Clip) => {
    setSelectedClip(clip);
  };

  return (
    <>
      <div className="rounded-xl bg-white">
        <div className="w-full">
          <div className="mb-2 flex items-center text-xl font-medium text-slate-950">
            AI Curated Content Feed For You
            <span className="ml-1 text-orange-500">
              <Icon name="IconSparkles" size={20} />
            </span>
          </div>
          <p className="text-sm text-slate-600">
            AI analyzes your company’s goals and video library to suggest the most relevant themes and clips — helping
            you find videos that drive engagement and align with your marketing priorities.
          </p>

          <div className="mt-6 space-y-6">
            {isLoading ? (
              <div className="space-y-8">
                <ClipCardSkeleton />
                <ClipCardSkeleton />
                <ClipCardSkeleton />
              </div>
            ) : isAllTopicsEmpty ? (
              <div className="mr-4">
                <ClipRecommendationEmptyState />
              </div>
            ) : (
              topicsArray.map((topicGroup: TopicGroup, topicIndex: number) => (
                <ClipSection
                  topicIndex={topicIndex}
                  key={topicIndex}
                  topicGroup={topicGroup}
                  onClipClick={handleClipClick}
                  hasLimitedContent={hasLimitedContent}
                />
              ))
            )}
          </div>
        </div>
      </div>
      {isPreviewModalOpen && (
        <ClipPreviewModal
          isOpen={isPreviewModalOpen}
          isLoading={isClipLoading}
          onClose={closePreviewModal}
          selectedClip={selectedClip}
          updateSelectedClip={handleUpdateSelectedClip}
        />
      )}
    </>
  );
};

export default ClipRecommendation;
